import { useState, useRef } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import { InputMask } from "@react-input/mask";
import { API_BASE } from "../../config";

export const SPECIALTIES = [
	"Pratisyen Hekim",
	"Aile Hekimliği",
	"İç Hastalıkları",
	"Geriatri",
	"Kardiyoloji",
	"Nöroloji",
	"Fizik Tedavi ve Rehabilitasyon",
	"Ortopedi ve Travmatoloji",
	"Psikiyatri",
	"Göğüs Hastalıkları",
	"Endokrinoloji",
	"Nefroloji",
	"Üroloji",
	"Genel Cerrahi",
	"Acil Tıp",
	"Anesteziyoloji ve Reanimasyon",
	"Göz Hastalıkları",
	"Kulak Burun Boğaz",
	"Dermatoloji",
	"Çocuk Sağlığı ve Hastalıkları",
	"Kadın Hastalıkları ve Doğum",
	"Diğer",
];

const DOCUMENTS = [
	{ key: "diploma", label: "Tıp Diploması", required: true },
	{ key: "specialtyDiploma", label: "Uzmanlık Diploması (varsa)", required: false },
	{
		key: "workCertificate",
		label: "Çalışma Belgesi (muayenehane ruhsatı / Tabip Odası belgesi)",
		required: true,
	},
];

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp";
const MAX_SIZE = 10 * 1024 * 1024;

export const docUrl = (doc) =>
	doc?.filename ? `${API_BASE}/uploads/doctor-docs/${doc.filename}` : null;

const emptyForm = (profile, user) => ({
	firstName: profile?.firstName || "",
	lastName: profile?.lastName || "",
	tcNo: profile?.tcNo || user?.tcNo || "",
	diplomaNo: profile?.diplomaNo || "",
	specialtyDiplomaNo: profile?.specialtyDiplomaNo || "",
	ministryRegistrationNo: profile?.ministryRegistrationNo || "",
	specialty: profile?.specialty || "",
	workAddress: profile?.workAddress || "",
	gsm: profile?.gsm || user?.phoneNumber || "",
	email: profile?.email || user?.mail || "",
});

/**
 * Doktor profil formu. Kaydedildiğinde backend profili yazar ve üyeyi doktor
 * olarak işaretler; profil eksikse backend reddeder.
 */
const DoctorProfileForm = ({ userId, user, profile, onSaved, onCancel }) => {
	const [form, setForm] = useState(() => emptyForm(profile, user));
	const [files, setFiles] = useState({ diploma: null, specialtyDiploma: null, workCertificate: null });
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const inputRefs = useRef({});

	const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

	const handleFile = (key, file) => {
		if (!file) {
			setFiles((prev) => ({ ...prev, [key]: null }));
			return;
		}
		if (file.size > MAX_SIZE) {
			setError("Belge boyutu en fazla 10 MB olabilir.");
			if (inputRefs.current[key]) inputRefs.current[key].value = "";
			return;
		}
		setError("");
		setFiles((prev) => ({ ...prev, [key]: file }));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");
		setSuccess("");

		// Zorunlu belgeler: yeni dosya veya daha önce yüklenmiş belge olmalı
		for (const doc of DOCUMENTS) {
			if (doc.required && !files[doc.key] && !profile?.documents?.[doc.key]?.filename) {
				setError(`${doc.label} yüklenmelidir.`);
				return;
			}
		}
		if (!/^\d{11}$/.test(form.tcNo.replace(/\D/g, ""))) {
			setError("TC numarası 11 haneli olmalıdır.");
			return;
		}

		const fd = new FormData();
		Object.entries(form).forEach(([k, v]) => fd.append(k, v));
		Object.entries(files).forEach(([k, f]) => {
			if (f) fd.append(k, f);
		});

		setSaving(true);
		try {
			const response = await fetch(`${API_BASE}/panel-users/${userId}/doctor-profile`, {
				method: "PUT",
				body: fd, // Content-Type tarayıcı tarafından (boundary ile) ayarlanır
			});
			let body = null;
			try {
				body = await response.json();
			} catch (_) {
				body = null;
			}
			if (!response.ok) {
				throw new Error(body?.message || "Doktor profili kaydedilemedi");
			}
			// Yeni yüklenen dosya seçimlerini temizle (artık sunucuda)
			setFiles({ diploma: null, specialtyDiploma: null, workCertificate: null });
			Object.values(inputRefs.current).forEach((el) => {
				if (el) el.value = "";
			});
			setSuccess(profile?.completedAt ? "Doktor bilgileri güncellendi." : "Doktor bilgileri kaydedildi.");
			onSaved?.(body);
		} catch (err) {
			setError(err.message || "Doktor profili kaydedilemedi");
		} finally {
			setSaving(false);
		}
	};

	return (
		<Form onSubmit={handleSubmit} className="doctor-form">
			<div className="doctor-form-grid">
				<Form.Group>
					<label className="mb-2">Ad *</label>
					<Form.Control
						type="text"
						placeholder="Ayşe"
						value={form.firstName}
						required
						onChange={(e) => set("firstName", e.target.value)}
					/>
				</Form.Group>
				<Form.Group>
					<label className="mb-2">Soyad *</label>
					<Form.Control
						type="text"
						placeholder="Yılmaz"
						value={form.lastName}
						required
						onChange={(e) => set("lastName", e.target.value)}
					/>
				</Form.Group>
				<Form.Group>
					<label className="mb-2">TC numarası *</label>
					<Form.Control
						type="text"
						placeholder="11 haneli"
						inputMode="numeric"
						maxLength={11}
						value={form.tcNo}
						required
						onChange={(e) => set("tcNo", e.target.value.replace(/\D/g, ""))}
					/>
				</Form.Group>
				<Form.Group>
					<label className="mb-2">Tıp diploması numarası *</label>
					<Form.Control
						type="text"
						placeholder="TD-____"
						value={form.diplomaNo}
						required
						onChange={(e) => set("diplomaNo", e.target.value)}
					/>
				</Form.Group>
				<Form.Group>
					<label className="mb-2">Uzmanlık diploması no (varsa)</label>
					<Form.Control
						type="text"
						placeholder="opsiyonel"
						value={form.specialtyDiplomaNo}
						onChange={(e) => set("specialtyDiplomaNo", e.target.value)}
					/>
				</Form.Group>
				<Form.Group>
					<label className="mb-2">Sağlık Bakanlığı tescil no *</label>
					<Form.Control
						type="text"
						placeholder="diplomanızın arkasında basılı / kaşenizde"
						value={form.ministryRegistrationNo}
						required
						onChange={(e) => set("ministryRegistrationNo", e.target.value)}
					/>
				</Form.Group>
				<Form.Group>
					<label className="mb-2">Uzmanlık alanı *</label>
					<Form.Select
						value={form.specialty}
						required
						onChange={(e) => set("specialty", e.target.value)}
					>
						<option value="">Seçiniz...</option>
						{SPECIALTIES.map((sp) => (
							<option value={sp} key={sp}>
								{sp}
							</option>
						))}
					</Form.Select>
				</Form.Group>
				<Form.Group>
					<label className="mb-2">Çalışma adresi *</label>
					<Form.Control
						type="text"
						placeholder="İlçe / il"
						value={form.workAddress}
						required
						onChange={(e) => set("workAddress", e.target.value)}
					/>
				</Form.Group>
				<Form.Group>
					<label className="mb-2">GSM *</label>
					<InputMask
						mask="05__ ___ __ __"
						replacement={{ _: /\d/ }}
						className="form-control"
						placeholder="05__ ___ __ __"
						value={form.gsm}
						required
						onChange={(e) => set("gsm", e.target.value)}
					/>
				</Form.Group>
				<Form.Group>
					<label className="mb-2">E-posta *</label>
					<Form.Control
						type="email"
						placeholder="dr@mail.com"
						value={form.email}
						required
						onChange={(e) => set("email", e.target.value)}
					/>
				</Form.Group>
			</div>

			<h6 className="doctor-form-section-title">Belge Yükleme</h6>
			{DOCUMENTS.map((doc) => {
				const existing = profile?.documents?.[doc.key];
				return (
					<Form.Group key={doc.key} className="mb-3">
						<label className="mb-2">
							{doc.label}
							{doc.required ? " *" : ""}
						</label>
						<Form.Control
							type="file"
							accept={ACCEPT}
							ref={(el) => (inputRefs.current[doc.key] = el)}
							onChange={(e) => handleFile(doc.key, e.target.files?.[0] || null)}
						/>
						{existing?.filename && !files[doc.key] && (
							<small className="text-muted d-block mt-1">
								Yüklü belge:{" "}
								<a href={docUrl(existing)} target="_blank" rel="noreferrer">
									{existing.originalName || existing.filename}
								</a>
								. Yeni dosya seçerseniz değiştirilir.
							</small>
						)}
					</Form.Group>
				);
			})}
			<small className="text-muted d-block mb-3">PDF, JPG, PNG veya WebP; en fazla 10 MB.</small>

			{error && <p className="text-danger">{error}</p>}
			{success && <p className="text-success">{success}</p>}

			<div className="doctor-form-actions">
				{onCancel && (
					<Button variant="secondary" type="button" onClick={onCancel} disabled={saving}>
						Vazgeç
					</Button>
				)}
				<Button variant="primary" type="submit" disabled={saving}>
					{saving ? (
						<>
							<Spinner animation="border" size="sm" style={{ marginRight: "8px" }} />
							Kaydediliyor...
						</>
					) : profile?.completedAt ? (
						"Doktor Bilgilerini Güncelle"
					) : (
						"Doktoru Ekle"
					)}
				</Button>
			</div>
		</Form>
	);
};

export default DoctorProfileForm;
